// /app/frontend/src/components/frugal-v2/components/UpgradeModal.jsx
// Upgrade modal: pick plan + open Stripe Payment Link

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Crown, Calendar, Mail, Camera } from 'lucide-react';
import { toast } from 'sonner';

const UpgradeModal = ({ isOpen, onClose, subscription, expired = false, t }) => {
  const [plan, setPlan] = useState('yearly');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSelect = async () => {
    setLoading(true);
    try {
      const url = await subscription.getPaymentLink(plan);
      if (url) {
        window.location.href = url;
      } else {
        toast.error('No se pudo abrir el link de pago');
        setLoading(false);
      }
    } catch (e) {
      toast.error('Error al obtener link de pago');
      setLoading(false);
    }
  };

  const monthlyPrice = subscription?.monthlyPrice ?? 2.99;
  const yearlyPrice = subscription?.yearlyPrice ?? 29.99;
  const savings = subscription?.yearlySavings ?? '17%';

  const content = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4 overflow-y-auto">
      <div className="bg-slate-800 rounded-2xl w-full max-w-md my-4 border border-emerald-500/20">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-400" />
            {expired ? 'Reactivar Zensaver' : 'Activar Plan Frugal'}
          </h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white" data-testid="upgrade-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {expired && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-sm text-rose-200">
              Tu trial ha terminado. Tus datos siguen guardados; al activar tu plan vuelves a tener acceso completo.
            </div>
          )}

          {/* Features */}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
            <div className="text-sm text-slate-300 font-medium mb-2">Con Zensaver Frugal:</div>
            <div className="space-y-2 text-sm text-slate-200">
              <div className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><span>Calculo automatico de Daily Zen</span></div>
              <div className="flex items-start gap-2"><Mail className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><span>Escaneo de correos bancarios</span></div>
              <div className="flex items-start gap-2"><Camera className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><span>OCR de recibos con IA</span></div>
              <div className="flex items-start gap-2"><Calendar className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><span>Reportes mensuales detallados</span></div>
            </div>
          </div>

          {/* Plans */}
          <div className="space-y-2">
            <button
              onClick={() => setPlan('yearly')}
              data-testid="plan-yearly-btn"
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                plan === 'yearly' ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 bg-slate-700/30'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold">Anual</span>
                    <span className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">AHORRA {savings}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">${(yearlyPrice / 12).toFixed(2)}/mes facturado anual</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-white">${yearlyPrice}</div>
                  <div className="text-xs text-slate-400">/año</div>
                </div>
              </div>
            </button>
            <button
              onClick={() => setPlan('monthly')}
              data-testid="plan-monthly-btn"
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                plan === 'monthly' ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 bg-slate-700/30'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-white font-semibold">Mensual</span>
                  <div className="text-xs text-slate-400 mt-0.5">Cancela cuando quieras</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-white">${monthlyPrice}</div>
                  <div className="text-xs text-slate-400">/mes</div>
                </div>
              </div>
            </button>
          </div>

          <button
            onClick={handleSelect}
            disabled={loading}
            data-testid="upgrade-pay-btn"
            className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold"
          >
            {loading ? 'Cargando...' : 'Continuar al pago'}
          </button>
          <p className="text-[11px] text-slate-500 text-center">Pago seguro procesado por Stripe</p>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default UpgradeModal;
